"use client";

import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "@heroui/react";
import { useLocale, useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import {
  LuHouse,
  LuInfo,
  LuLanguages,
  LuPlus,
  LuTrophy,
  LuX,
} from "react-icons/lu";

import { updateUserLanguage } from "@/actions/language";
import { useAuth } from "@/contexts/AuthContext";
import { useFeedbackPrompt } from "@/contexts/FeedbackPromptContext";
import { Link, usePathname } from "@/i18n/routing";
import { MAIN_NAV_ITEMS } from "@/lib/navigation-config";
import type { NavDrawerProps } from "@/types/navigation";

import { TornihaasteMark } from "./TornihaasteMark";

const ICON_MAP = {
  home: LuHouse,
  trophy: LuTrophy,
  info: LuInfo,
  plus: LuPlus,
} as const;

export function NavDrawer({ isOpen, onClose }: NavDrawerProps) {
  const t = useTranslations();
  const pathname = usePathname();
  const locale = useLocale() as "fi" | "en";
  const { user } = useAuth();
  const { openFeedback } = useFeedbackPrompt();

  const handleFeedbackClick = () => {
    onClose();
    openFeedback();
  };

  const handleLanguageSwitch = async () => {
    const newLocale = locale === "fi" ? "en" : "fi";
    if (user?.id) {
      await updateUserLanguage(newLocale);
    }
    const currentPath = window.location.pathname;
    let newPath: string;
    if (currentPath.startsWith("/fi/") || currentPath.startsWith("/en/")) {
      newPath = `/${newLocale}${currentPath.slice(3)}`;
    } else if (currentPath === "/fi" || currentPath === "/en") {
      newPath = `/${newLocale}`;
    } else {
      newPath = `/${newLocale}${currentPath}`;
    }
    window.location.href = newPath;
  };

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      placement="left"
      size="xs"
      hideCloseButton
      classNames={{
        base: "bg-tn-bg-panel",
        backdrop: "bg-black/70 backdrop-blur-[3px]",
      }}
    >
      <DrawerContent>
        <DrawerHeader className="border-b border-tn-border px-[18px] py-4">
          <div className="flex flex-1 items-center gap-2.5">
            <TornihaasteMark size={36} />
            <span className="font-display text-base font-bold uppercase tracking-[0.06em] text-tn-gold">
              Tornihaaste
            </span>
          </div>
          <Button
            isIconOnly
            variant="bordered"
            radius="md"
            size="sm"
            aria-label={t("nav.aria.closeMainMenu")}
            onPress={onClose}
            className="border-tn-border bg-transparent text-tn-text-secondary"
          >
            <LuX size={16} />
          </Button>
        </DrawerHeader>

        <DrawerBody className="px-0 py-0">
          <div className="px-[18px] pt-4 pb-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-tn-text-muted">
            {t("nav.drawer.sectionLabel")}
          </div>

          {MAIN_NAV_ITEMS.map((item) => {
            const Icon = ICON_MAP[item.iconName];
            const isActive =
              pathname === item.href ||
              (item.href !== "/" && pathname.startsWith(`${item.href}/`));
            return (
              <Link
                key={item.id}
                href={item.href}
                onClick={onClose}
                className={
                  "flex items-center gap-3 border-l-[3px] px-[18px] py-3 font-display text-[12.5px] font-semibold uppercase tracking-[0.1em] transition-colors " +
                  (isActive
                    ? "border-tn-gold bg-tn-gold/5 text-tn-gold"
                    : "border-transparent text-tn-text-secondary hover:bg-tn-elevated hover:text-tn-text-primary")
                }
              >
                <span
                  className={
                    "flex size-6 items-center justify-center " +
                    (isActive ? "text-tn-gold" : "text-tn-text-muted")
                  }
                >
                  <Icon size={18} />
                </span>
                {t(item.labelKey)}
              </Link>
            );
          })}
        </DrawerBody>

        <DrawerFooter className="flex-col gap-2 border-t border-tn-border px-[18px] py-3.5">
          {user && (
            <Button
              variant="bordered"
              radius="md"
              size="sm"
              fullWidth
              startContent={<Icon icon="ix:feedback" width={14} />}
              className="border-tn-gold/40 bg-tn-gold/10 text-tn-gold hover:bg-tn-gold/15"
              onPress={handleFeedbackClick}
            >
              {t("feedback.giveFeedback")}
            </Button>
          )}
          <Button
            variant="bordered"
            radius="md"
            size="sm"
            fullWidth
            startContent={<LuLanguages size={13} />}
            className="border-tn-border bg-white/[0.04] text-tn-text-secondary"
            aria-label={t("nav.aria.language")}
            onPress={handleLanguageSwitch}
          >
            {locale === "fi" ? "In English" : "Suomeksi"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
